/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

// This function helps to load the new image instead of loading from cache
// if the image in image url is updated

export const validatePreviousDate =
  (t) =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      const current = getFieldValue(['period', 'currentReadingDate']);

      if (!value || !current) {
        return Promise.resolve();
      }

      if (value.isAfter(current)) {
        return Promise.reject(
          t(
            'label_previous_reading_date_must_be_before_current_reading_date_error',
          ),
        );
      }

      const daysDiff = current.diff(value, 'days');

      if (daysDiff >= 367) {
        return Promise.reject(
          t('label_reading_gap_should_be_less_than_year_error'),
        );
      }

      return Promise.resolve();
    },
  });

export const validateCurrentDate =
  (t) =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      const previous = getFieldValue(['period', 'previousReadingDate']);

      if (!value || !previous) {
        return Promise.resolve();
      }

      if (!value.isAfter(previous)) {
        return Promise.reject(
          t(
            'label_current_reading_date_must_be_after_previous_reading_date_error',
          ),
        );
      }

      const daysDiff = value.diff(previous, 'days');

      if (daysDiff >= 367) {
        return Promise.reject(
          t('label_reading_gap_should_be_less_than_year_error'),
        );
      }

      return Promise.resolve();
    },
  });

export const validatePreviousReading =
  (currentReadingPath, t) =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      const currentValue = getFieldValue(currentReadingPath);

      if (value == null || currentValue == null) return Promise.resolve();

      const current = Number(currentValue);
      const previous = Number(value);

      if (previous < 0) {
        return Promise.reject(t('label_meter_reading_positive_number_error'));
      }
      if (previous > current) {
        return Promise.reject(
          t('label_meter_reading_less_than_current_error'),
        );
      }

      if (current - previous >= 1_000_000) {
        return Promise.reject(
          t('label_reading_difference_should_be_less_than_million_error'),
        );
      }

      return Promise.resolve();
    },
  });

export const validateCurrentReading =
  (previousReadingPath, t) =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      const previousValue = getFieldValue(previousReadingPath);

      if (value == null || previousValue == null) return Promise.resolve();

      const current = Number(value);
      const previous = Number(previousValue);

      if (current < 0) {
        return Promise.reject(t('label_meter_reading_positive_number_error'));
      }
      if (current < previous) {
        return Promise.reject(t('label_meter_reading_less_than_current_error'));
      }

      if (current - previous >= 1_000_000) {
        return Promise.reject(
          t('label_reading_difference_should_be_less_than_million_error'),
        );
      }

      return Promise.resolve();
    },
  });
